<template>
  <form action="none" class="GatedContentForm" @submit.prevent="submitForm">
    <label @change="fullNameCheckInvalid" @input="fullNameCheckValid" :class="fullNameFieldStatus">
      <div class="fieldTitle">{{ localeStrings.fullNameTitle }}</div>
      <input
        v-model="fullNameFieldValue"
        :placeholder="localeStrings.fullNamePrompt"
        class="fieldInput"
        :class="fullNameFieldStatus"
        v-bind="{ disabled }"
      >
      <div class="fieldErrorMessage" :hidden="fullNameFieldStatus !== 'invalid'">
        {{ localeStrings.fullNameErrorMessage }}
      </div>
    </label>
    <label @change="emailCheckInvalid" @input="emailCheckValid" :class="emailFieldStatus">
      <div class="fieldTitle">{{ localeStrings.emailTitle }}</div>
      <input
        v-model="emailFieldValue"
        :placeholder="localeStrings.emailPrompt"
        class="fieldInput"
        :class="emailFieldStatus"
        v-bind="{ disabled }"
      >
      <div class="fieldErrorMessage" :hidden="emailFieldStatus !== 'invalid'">
        {{ localeStrings.emailErrorMessage }}
      </div>
    </label>
    <p class="privacyNotice" v-html="privacyNoticeHtml"></p>
    <div class="formFooter">
      <CtaButton
        icon="download"
        type="submit"
        v-bind="{ disabled }"
        :text="buttonText"
        class="button --bright-blue"
        :hidden="submissionStatus === 'submitted'"
      />
      <div class="statusMessageSubmitted" :hidden="submissionStatus !== 'submitted'">
        <span class="submittedText">{{ localeStrings.confirmationText }}</span>
      </div>
      <div class="statusMessageFailed" :hidden="submissionStatus !== 'failed'">
        {{ localeStrings.submissionErrorText }}
      </div>
    </div>
  </form>
</template>

<script>
import { pushAnalyticsEvent } from '~/lib/analyticsEvents.js'
import submitForm from '~/lib/submitForm.js'

import strings from './strings.json'

function testEmail(value) {
  const regExp = /^[a-zA-Z0-9_.+-]+@([a-zA-Z0-9-]+\.)+[a-zA-Z0-9]{2,24}$/
  return regExp.test(value.trim())
}

export default {
  props: {
    buttonText: {
      type: String,
    },
    locale: {
      type: String,
    },
    requestedContent: {
      type: String,
    },
  },
  computed: {
    disabled() {
      return this.submissionStatus === 'submitting'
    },
    localeStrings() {
      return strings[this.locale] ?? {}
    },
    privacyNoticeHtml() {
      const { privacyNotice, privacyUrl } = strings[this.locale]
      return privacyNotice.replaceAll(/\{(.*?)\}/g, (_, text) => `<a href="${privacyUrl}">${text}</a>`)
    },
  },
  data() {
    return {
      submissionStatus: 'unsubmitted',
      fullNameFieldStatus: 'pending',
      fullNameFieldValue: '',
      emailFieldStatus: 'pending',
      emailFieldValue: '',
    }
  },
  methods: {
    fullNameCheckValid() { if (this.fullNameFieldValue.trim() !== '') this.fullNameFieldStatus = 'valid' },
    fullNameCheckInvalid() { if (this.fullNameFieldValue.trim() === '') this.fullNameFieldStatus = 'invalid' },
    emailCheckValid() { if (testEmail(this.emailFieldValue)) this.emailFieldStatus = 'valid' },
    emailCheckInvalid() { if (!testEmail(this.emailFieldValue)) this.emailFieldStatus = 'invalid' },
    submitForm() {
      if (typeof window !== 'undefined') {
        this.fullNameCheckInvalid()
        this.emailCheckInvalid()
        if (this.fullNameFieldStatus === 'valid' && this.emailFieldStatus === 'valid') {
          const submissionData = {
            requestedResource: this.requestedContent,
            fullName: this.fullNameFieldValue,
            email: this.emailFieldValue,
            incentive: this.buttonText,
            digitalLeadType: "Nurture campaign ebook"
          }
          this.submissionStatus = 'submitting'
          submitForm({ variant: 'ebook', locale: this.locale }, submissionData)
            .then((response) => {
              if (response.ok) {
                this.submissionStatus = 'submitted'
                pushAnalyticsEvent({
                  scope: 'gatedContentDownloadModule',
                  action: 'formConversion',
                  content: {
                    buttonText: this.buttonText,
                    requestedContent: this.requestedContent,
                  },
                })
              } else {
                this.submissionStatus = 'failed'
              }
            })
            .catch(() => {
              this.submissionStatus = 'failed'
            })
        }
      }
    },
  },
}
</script>

<style scoped>

.GatedContentForm {
  --error-color: #f33;
}

.GatedContentForm label {
  display: block;
  margin-bottom: var(--spacing-5);
}

.fieldTitle {
  font-weight: 600;
  font-size: var(--font-size-2);
  color: var(--heading-color);
}

.fieldInput {
  margin-top: 0.25rem;
  width: 100%;
  border-style: none;
  border-radius: 8px;
  padding: var(--spacing-4) var(--spacing-7);
  font-size: var(--font-size-2);
  color: var(--text-color-black);
}

.fieldInput.invalid {
  outline: thin solid var(--error-color);
}

.fieldInput::placeholder {
  color: rgba(0, 0, 0, 40%);
}

.fieldErrorMessage {
  margin-top: var(--spacing-0);
  font-size: var(--font-size-1);
  color: var(--error-color);
}

.formFooter {
  margin-top: var(--spacing-6);
}

.button[hidden] {
  display: none;
}

.statusMessageSubmitted::before {
  content: url("./images/success.svg");
  float: left;
  margin-right: var(--spacing-3);
}

.submittedText {
  font-weight: 500;
  font-size: var(--font-size-3);
  color: var(--heading-color);
}

.privacyNotice, .statusMessageFailed {
  margin-top: var(--spacing-5);
  font-size: var(--font-size-1);
  color: var(--heading-color);
}

.privacyNotice:deep(a) {
  text-decoration: underline;
  color: var(--brand-color);
}

.privacyNotice:deep(a:hover) {
  filter: brightness(0.85);
}
</style>
